import { FileTextOutlined, ReloadOutlined, StopOutlined, UndoOutlined } from '@ant-design/icons';
import { Button, Modal, Popconfirm, Space, Spin, Typography, message } from 'antd';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import WebGrid from '../../webgrid/src';
import EInvoiceStatusChip from '../components/eInvoice/EInvoiceStatusChip';
import { useCashDrawer } from '../contexts/CashDrawerContext';
import { useEInvoiceStatuses } from '../hooks/useEInvoiceStatuses';
import { useInvoicePreview } from '../hooks/useInvoicePreview';
import useRequest from '../hooks/useRequest';
import useCashBookingReadiness from '../hooks/useCashBookingReadiness';
import { CashBookingRefusalNotice } from '../components/CashBookingRefusal';
import {
    formatStornoAmount,
    INVOICE_TYPE_GOODS_SALE,
    stornoRowKey,
    type StornoInvoice,
    type StornoResult,
} from '../domain/storno';
import { createTextColumn } from '../domain/gridColumns';

const { Title, Text } = Typography;

interface ListResponse {
    success: boolean;
    data?: StornoInvoice[];
    message?: string;
}

interface StornoResponse {
    success: boolean;
    data?: StornoResult;
    message?: string;
}

const Storno = () => {
    const { t } = useTranslation();
    const request = useRequest();
    const { cashPointId } = useCashDrawer();
    // The refund leaves this drawer: a station that cannot book it sees why on opening the page.
    const { refusalKey: bookingRefusalKey } = useCashBookingReadiness({ checkOnMount: true });
    const [invoices, setInvoices] = useState<StornoInvoice[]>([]);
    const [loading, setLoading] = useState(false);
    const [processing, setProcessing] = useState<string | null>(null);
    const { available: eInvoiceAvailable, rowsByInvoice } = useEInvoiceStatuses(
        invoices.map(invoice => invoice.id),
    );

    const {
        invoicePreview,
        invoicePreviewLoading,
        invoicePreviewError,
        showInvoicePreview,
        closeInvoicePreview,
    } = useInvoicePreview(t);

    const loadInvoices = useCallback(async () => {
        setLoading(true);
        try {
            const response = await request<ListResponse>({ path: '/storno/invoices' });
            if (response.success) {
                setInvoices(response.data ?? []);
            } else {
                message.error(response.message ?? t('storno.loadFailed'));
            }
        } catch {
            message.error(t('storno.loadFailed'));
        } finally {
            setLoading(false);
        }
    }, [request, t]);

    useEffect(() => {
        loadInvoices();
    }, [loadInvoices]);

    const handleStorno = useCallback(
        async (invoice: StornoInvoice) => {
            const invoiceNumber = invoice.invoiceNumber;
            if (!invoiceNumber) {
                return;
            }
            // The refund leaves this workstation's drawer, so the register has
            // to be known before the credit note can be attributed to a session.
            if (cashPointId === null) {
                message.error(t('storno.noCashPoint'));
                return;
            }
            setProcessing(invoiceNumber);
            try {
                const response = await request<StornoResponse>({
                    path: `/storno/invoices/${encodeURIComponent(invoiceNumber)}/storno`,
                    method: 'POST',
                    params: { cashPointId },
                });
                if (response.success && response.data) {
                    const stornoNumber = response.data.stornoInvoiceNumber;
                    message.success(t('storno.success', { invoiceNumber: stornoNumber }));
                    await loadInvoices();
                    // Show the freshly issued credit note document.
                    await showInvoicePreview(stornoNumber, 'storno.previewError', 'Storno');
                } else {
                    message.error(response.message ?? t('storno.actionFailed'));
                }
            } catch (error) {
                message.error(error instanceof Error ? error.message : t('storno.actionFailed'));
            } finally {
                setProcessing(null);
            }
        },
        [request, t, loadInvoices, showInvoicePreview, cashPointId],
    );

    const columns = useMemo(
        () => [
            createTextColumn<StornoInvoice>('invoiceNumber', t('storno.invoiceNumber')),
            createTextColumn<StornoInvoice>('invoiceDate', t('storno.invoiceDate')),
            createTextColumn<StornoInvoice>('contractNumber', t('storno.contractNumber')),
            createTextColumn<StornoInvoice>('itemName', t('storno.itemName')),
            createTextColumn<StornoInvoice>('clientName', t('storno.clientName')),
            {
                key: 'amount',
                title: t('storno.amount'),
                dataIndex: 'amount',
                sortable: true,
                filterable: false,
                render: (amount: unknown) => `${formatStornoAmount(amount as number)} LEI`,
            },
            ...(eInvoiceAvailable
                ? [
                      {
                          key: 'eInvoice',
                          title: t('eInvoice.chip.column'),
                          dataIndex: 'eInvoice',
                          sortable: false,
                          filterable: false,
                          render: (_value: unknown, invoice: StornoInvoice) => (
                              <EInvoiceStatusChip rows={rowsByInvoice.get(invoice.id)} />
                          ),
                      },
                  ]
                : []),
            {
                key: 'actions',
                title: t('storno.actions'),
                dataIndex: 'actions',
                sortable: false,
                filterable: false,
                render: (_value: unknown, invoice: StornoInvoice) => (
                    <Space>
                        <Button
                            size="small"
                            icon={<FileTextOutlined />}
                            onClick={() =>
                                invoice.invoiceNumber &&
                                showInvoicePreview(
                                    invoice.invoiceNumber,
                                    'storno.previewError',
                                    INVOICE_TYPE_GOODS_SALE,
                                )
                            }
                            data-testid={`storno-preview-${stornoRowKey(invoice)}`}
                        >
                            {t('storno.preview')}
                        </Button>
                        <Popconfirm
                            title={t('storno.confirmTitle')}
                            description={t('storno.confirmDescription', {
                                invoiceNumber: invoice.invoiceNumber,
                            })}
                            okText={t('storno.confirmButton')}
                            cancelText={t('storno.cancel')}
                            okButtonProps={{ danger: true }}
                            onConfirm={() => handleStorno(invoice)}
                        >
                            <Button
                                size="small"
                                danger
                                icon={<StopOutlined />}
                                loading={processing === invoice.invoiceNumber}
                                disabled={bookingRefusalKey !== null}
                                data-testid={`storno-action-${stornoRowKey(invoice)}`}
                            >
                                {t('storno.storno')}
                            </Button>
                        </Popconfirm>
                    </Space>
                ),
            },
        ],
        [
            t,
            processing,
            showInvoicePreview,
            handleStorno,
            eInvoiceAvailable,
            rowsByInvoice,
            bookingRefusalKey,
        ],
    );

    let previewContent = (
        <Text type="danger">{invoicePreviewError ?? t('storno.previewError')}</Text>
    );
    if (invoicePreviewLoading) {
        previewContent = (
            <div
                aria-label={t('storno.previewLoading')}
                style={{ display: 'flex', justifyContent: 'center', paddingTop: 120 }}
            >
                <Spin size="large" />
            </div>
        );
    } else if (invoicePreview?.url) {
        previewContent = (
            <iframe
                title={t('storno.previewTitle', { invoiceNumber: invoicePreview.invoiceNumber })}
                src={invoicePreview.url}
                style={{ border: 0, height: '100%', width: '100%' }}
            />
        );
    }

    return (
        <div style={{ padding: 16 }} data-testid="storno-page">
            <div
                className="page-header"
                style={{ ['--page-header-accent' as string]: '#57534e' }}
            >
                <div className="page-header__lead">
                    <span className="page-header__icon">
                        <UndoOutlined aria-hidden />
                    </span>
                    <div>
                        <Title level={2} style={{ margin: 0 }}>
                            {t('storno.title')}
                        </Title>
                        <Text type="secondary">{t('storno.subtitle')}</Text>
                    </div>
                </div>
                <div className="page-header__actions">
                    <Button icon={<ReloadOutlined />} onClick={loadInvoices} disabled={loading}>
                        {t('storno.refresh')}
                    </Button>
                </div>
            </div>

            <CashBookingRefusalNotice refusalKey={bookingRefusalKey} />

            <div data-testid="storno-table">
                <WebGrid<StornoInvoice>
                    data={invoices}
                    columns={columns}
                    loading={loading}
                    filterBar
                    pagination
                    pageSize={20}
                    rowIdAccessor={stornoRowKey}
                    emptyMessage={t('storno.empty')}
                />
            </div>

            <Modal
                title={t('storno.previewTitle', {
                    invoiceNumber: invoicePreview?.invoiceNumber ?? '',
                })}
                open={invoicePreview !== null}
                onCancel={closeInvoicePreview}
                footer={null}
                width="80%"
                styles={{ body: { height: '75vh' } }}
            >
                {previewContent}
            </Modal>
        </div>
    );
};

export default Storno;
